import { useState, useEffect, type ReactNode } from "react";
import {
  callFetchPickups,
  callCreatePickup,
  callUpdatePickup,
  callDeletePickup,
} from "../../../api/pickups";
import {
  UserPickupContext,
  type UserPickupContextType,
} from "./UserPickupContext";
import type {
  CreatePickupPayload,
  Pickup,
  UpdatePickupPayload,
} from "../../../api/pickups";
import { useAuthUser } from "../hooks/useAuthUser";

interface UserPickupProviderProps {
  children: ReactNode;
}

export const UserPickupContextProvider = ({
  children,
}: UserPickupProviderProps) => {
  const { userId } = useAuthUser();
  const [pickup, setPickup] = useState<Pickup | null>(null);
  const [loading, setLoading] = useState(() => Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  const fetchPickup = async (userIdToFetch: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const list = await callFetchPickups();
      const found =
        list.find((pickup) => pickup.userId === userIdToFetch) ?? null;
      setPickup(found);
    } catch (err) {
      console.error("Error fetching user pickup:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch user pickup",
      );
      setPickup(null);
    } finally {
      setLoading(false);
    }
  };

  const createPickup = async (data: CreatePickupPayload): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const created = await callCreatePickup(data);
      setPickup(created);
    } catch (err) {
      console.error("Error adding user pickup:", err);
      setError(
        err instanceof Error ? err.message : "Failed to add user pickup",
      );
    } finally {
      setLoading(false);
    }
  };

  const updatePickup = async (
    id: string,
    data: UpdatePickupPayload,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await callUpdatePickup(id, data);
      setPickup(updated);
    } catch (err) {
      console.error("Error updating user pickup:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update user pickup",
      );
    } finally {
      setLoading(false);
    }
  };

  const deletePickup = async (): Promise<void> => {
    if (!pickup) {
      setError("No pickup to delete");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await callDeletePickup(pickup.id);
      setPickup(null);
    } catch (err) {
      console.error("Error deleting user pickup:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete user pickup",
      );
    } finally {
      setLoading(false);
    }
  };

  const refreshPickup = async (userIdToFetch: string): Promise<void> => {
    await fetchPickup(userIdToFetch);
  };

  useEffect(() => {
    if (userId) {
      setLoading(true);
      void fetchPickup(userId);
    } else {
      setPickup(null);
      setLoading(false);
    }
  }, [userId]);

  const value: UserPickupContextType = {
    pickup,
    loading,
    error,
    fetchPickup,
    createPickup,
    updatePickup,
    deletePickup,
    refreshPickup,
  };

  return (
    <UserPickupContext.Provider value={value}>
      {children}
    </UserPickupContext.Provider>
  );
};
